import axios, { AxiosHeaders, AxiosRequestConfig } from "axios";
import { getAuthTokenValue } from "@kaihong/utils/lib/khAuthToken";

/** Only the business-rule statistic requires the rule backend's tenant header. */
export const isRuleStatisticRequest = (
  url: string,
  params: AxiosRequestConfig = {}
): boolean => {
  if (String(params.method || "get").toUpperCase() !== "POST") return false;
  try {
    const target = new URL(
      axios.getUri({ url, baseURL: params.baseURL, allowAbsoluteUrls: params.allowAbsoluteUrls }),
      window.location.origin
    );
    return target.origin === window.location.origin
      && target.pathname === "/api/rule-editor/rule/app/page";
  } catch {
    return false;
  }
};

const currentToken = (): string =>
  localStorage.getItem("X-Access-Token")
  || getAuthTokenValue("X-Access-Token")
  || "";

const validTenant = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0 && value.length <= 64
  && value === value.trim() && Array.from(value).every((character) => {
    const code = character.charCodeAt(0);
    return code >= 32 && (code < 127 || code > 159);
  });

let pending: { token: string; promise: Promise<string> } | undefined;

/** Merge only concurrent confirmations; do not retain a tenant after switching. */
const confirmedTenant = async (token: string): Promise<string> => {
  if (pending?.token === token) return pending.promise;
  const promise = axios.get("/api/auth/tenant/current", {
    headers: { "X-Access-Token": token, "Cache-Control": "no-cache, no-store" },
    withCredentials: true,
    timeout: 8000,
  }).then(({ data }) => {
    const wrapped = data && typeof data === "object"
      && ("status" in data || "result" in data);
    if (wrapped && data.status !== 200) {
      throw new Error("无法确认当前租户，请刷新后重试");
    }
    const current = wrapped ? data.result : data;
    if (current?.tenantIsolationExempt !== false || !validTenant(current?.tenantId)) {
      throw new Error("当前租户工作区不可用，请先选择租户");
    }
    return current.tenantId as string;
  });
  const entry = { token, promise };
  pending = entry;
  try {
    return await promise;
  } finally {
    if (pending === entry) pending = undefined;
  }
};

export const withRuleStatisticTenant = async (
  params: AxiosRequestConfig = {}
): Promise<AxiosRequestConfig> => {
  const token = currentToken();
  if (!token) throw new Error("请先登录");
  const tenantId = await confirmedTenant(token);
  if (currentToken() !== token) {
    throw new Error("登录状态已变化，请刷新后重试");
  }
  const headers = new AxiosHeaders(params.headers as AxiosHeaders | undefined);
  headers.set("X-Access-Token", token, true);
  headers.set("X-Tenant-Id", tenantId, true);
  return { ...params, headers };
};
